// Distribution Main Page - Refactored with Modal

import React from 'react';
import {Tabs, TabItem} from '../../../Presentation/components/ui/Tabs';
import {useDistributionManagement} from '../hooks/useDistributionManagement';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {AppTitle} from '../../../Presentation/components/ui/Typography';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import Button from '../../../Presentation/components/ui/Button';
import Modal from '../../../Presentation/components/ui/Modal';
import {FileText, TruckIcon, BarChart3} from 'lucide-react';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

// Import components
import MarketerDeliveryTab from '../components/MarketerDeliveryTab';
import StatsOverviewTab from '../components/StatsOverviewTab';
import DistributionTable from '../components/DistributionTable';

const DistributionPage: React.FC = () => {
  const {hasPermission} = useAuthorization();

  // Unified distribution management
  const {
    // UI State
    activeTab, isDeliveryModalOpen, openDeliveryModal, closeDeliveryModal,

    // Data
    distributionHistory, marketers, products, productAvailableSerials,
    pagination,

    // Loading States
    isLoading,

    // Error States
    error,

    // Actions
    handleTabChange, handlePageChange, handlePageSizeChange, handleDeliveryFormSubmit, handleDeleteTransaction,

    // Data Fetching
    fetchMarketers, fetchProducts, fetchAvailableSerialsForProduct, clearProductAvailableSerials
  } = useDistributionManagement();

  // Check if user has access to distribution
  if (!hasPermission(PERMISSIONS.READ_DISTRIBUTION)) {
    return (
      <SidebarLayout>
        <div className="p-6 min-h-screen rounded-xl bg-[#F2F3F5]">
          <PermissionDeniedCard message="ليس لديك صلاحية للوصول إلى إدارة التوزيع" />
        </div>
      </SidebarLayout>
    );
  }

  const tabItems: TabItem[] = [
    {
      key: 'history',
      label: 'سجل التوزيع',
      description: 'عرض سجل عمليات التوزيع',
      icon: <FileText className="w-5 h-5" />,
      badge: pagination?.total ?? 0,
      content: (
        <div className="space-y-6">
          {/* Records Table - Full Width */}
          <Card className="overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-800">سجل التوزيع</h3>
              </div>
            </div>

            {isLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-4"></div>
                <p className="text-gray-600">جاري تحميل سجل التوزيع...</p>
              </div>
            ) : (
              <DistributionTable
                records={distributionHistory}
                pageSize={pagination?.limit || 20}
                currentPage={pagination?.page || 1}
                totalCount={pagination?.total || 0}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onDeleteTransaction={handleDeleteTransaction}
                exportable={true}
              />
            )}
          </Card>
        </div>
      )
    },
    {
      key: 'stats',
      label: 'الإحصائيات',
      description: 'إحصائيات التوزيع والتحليلات',
      icon: <BarChart3 className="w-5 h-5" />,
      content: (
        <StatsOverviewTab />
      )
    }
  ];

  return (
    <SidebarLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <AppTitle>إدارة التوزيع</AppTitle>
            <p className="text-gray-600 mt-1">توزيع المنتجات على المسوقين</p>
          </div>

          {/* Add Distribution Button */}
          {hasPermission(PERMISSIONS.CREATE_DISTRIBUTION) && (
            <Button
              onClick={openDeliveryModal}
              icon={<TruckIcon className="w-4 h-4" />}
              iconPosition="right"
            >
              توزيع جديد
            </Button>
          )}
        </div>

        {/* Tabs Container */}
        <Card className="p-0 overflow-hidden">
          <Tabs
            items={tabItems}
            defaultActiveKey={activeTab}
            className="w-full"
            onChange={handleTabChange}
            variant="modern"
          />
        </Card>

        {/* Delivery Modal */}
        <Modal
          open={isDeliveryModalOpen}
          onClose={closeDeliveryModal}
          title="توزيع المنتجات على المسوقين"
          width="max-w-6xl"
        >
          <MarketerDeliveryTab
            marketers={marketers}
            products={products}
            availableSerials={productAvailableSerials}
            error={error}
            isLoading={isLoading}
            onSubmit={handleDeliveryFormSubmit}
            onFetchMarketers={fetchMarketers}
            onFetchProducts={fetchProducts}
            onFetchAvailableSerials={fetchAvailableSerialsForProduct}
            onClearAvailableSerials={clearProductAvailableSerials}
          />
        </Modal>
      </div>
    </SidebarLayout>
  );
};

export default DistributionPage;
